import { getQuest, type SpellDefinition } from '@shefira/shared';

import { element } from './kit.js';
import { spellCost } from './spell-bar.js';
import { uiFormat, uiText } from './text.js';

/** Drag data of the grimoire: which spell, and the slot it left when it came from the bar. */
export const DRAG_TYPE = 'application/x-shefira-spell';

export interface SpellRowState {
  known: boolean;
  onBar: boolean;
  /** Picked up by a click or a tap, waiting for a slot. */
  picked: boolean;
  /** The character's level, for the requirement of a spell still locked. */
  level: number;
  /** A mouse drags the spell to a slot; a finger taps it instead (ADR 0043). */
  draggable: boolean;
  onPick: () => void;
}

/**
 * One spell of the grimoire (ADR 0048): its icon, name and description,
 * then its cost and cooldown when known, or what teaches it when not.
 */
export function spellRow(spell: SpellDefinition, state: SpellRowState): HTMLLIElement {
  const row = element('li', 'spellbook__row');
  row.dataset.known = String(state.known);
  if (state.picked) row.classList.add('spellbook__row--picked');

  const icon = element('button', 'spellbook__spell');
  icon.type = 'button';
  icon.disabled = !state.known;
  const art = element('span', 'spellbook__icon');
  art.style.backgroundImage = `url('/assets/ui/${spell.icon}.png')`;
  icon.append(art);
  const label = state.known
    ? uiFormat('Mettre {name} dans la barre', { name: spell.name })
    : spell.name;
  icon.title = uiText(label);
  icon.setAttribute('aria-label', uiText(label));

  const text = element('div', 'spellbook__text');
  const head = element('div', 'spellbook__head');
  head.append(element('span', 'spellbook__name', spell.name));
  if (state.onBar) head.append(element('span', 'spellbook__badge', 'Dans la barre'));
  text.append(head, element('p', 'spellbook__description', spell.description));
  const meta = element('p', 'spellbook__meta');
  if (state.known) meta.textContent = uiText(spellCost(spell));
  else meta.append(...requirement(spell, state.level));
  text.append(meta);

  row.append(icon, text);
  if (state.known) {
    row.addEventListener('click', state.onPick);
    if (state.draggable) {
      row.draggable = true;
      row.addEventListener('dragstart', (event) => {
        event.dataTransfer?.setData(DRAG_TYPE, JSON.stringify({ id: spell.id }));
        if (event.dataTransfer !== null) event.dataTransfer.effectAllowed = 'copyMove';
      });
    }
  }
  return row;
}

/** « Niveau 12 · Quête : Les rôdeuses des dunes », the part already met in green. */
function requirement(spell: SpellDefinition, level: number): HTMLElement[] {
  const parts: HTMLElement[] = [];
  const need = element(
    'span',
    'spellbook__need',
    uiFormat('Niveau {level}', { level: spell.learn.level }),
  );
  if (level >= spell.learn.level) need.classList.add('spellbook__need--met');
  parts.push(need);
  const questId = spell.learn.quest;
  if (questId !== undefined) {
    const name = getQuest(questId)?.name ?? questId;
    parts.push(element('span', 'spellbook__need', uiFormat('Quête : {name}', { name })));
  }
  return parts;
}

/** What a drop carries, read back without trusting it: a spell id, and the slot it left. */
export function parseDrag(raw: string): { id: string; index?: number } | null {
  try {
    const value: unknown = JSON.parse(raw);
    if (typeof value !== 'object' || value === null) return null;
    const id = 'id' in value && typeof value.id === 'string' ? value.id : null;
    if (id === null) return null;
    const index = 'index' in value && typeof value.index === 'number' ? value.index : undefined;
    return index === undefined ? { id } : { id, index };
  } catch {
    return null;
  }
}
